import { Link } from "react-router-dom";
import { BookOpenText, Mail } from "lucide-react";
import useReveal from "../../components/useReveal";
import ScrollScrubVideo from "../../components/ScrollScrubVideo";
import ResponsiveHeroVideo from "../../components/ResponsiveHeroVideo";
import { editorialArticles } from "../../content/news";

const AX_CONCEPT_CAPTIONS = [
  { at: 0.1, text: "Ateno AX — designed as a coprocessor", icon: "chip" },
  { at: 0.55, text: "For 3D Gaussian Splatting workloads.", icon: "zap" },
  { at: 0.9, text: "Currently an FPGA prototype. Dedicated silicon is the next step.", icon: "layers" },
];

const AX_CONCEPT_DISCLOSURE = "Concept visualization — Ateno AX is in development";

export default function SpatialAcceleratorPage() {
  useReveal();

  return (
    <div className="product-family-page">
      <section className="product-family-hero product-family-hero-hardware photo-section">
        <div className="hero-video-layer" aria-hidden="true">
          <ResponsiveHeroVideo
            className="hero-video product-family-hardware-video"
            desktopSrc="/assets/media/hero/hero-optimized.mp4"
            mobileSrc="/assets/media/hero/hero-mobile.mp4"
            poster="/assets/media/hero/hero-poster-optimized.jpg"
          />
        </div>
        <div className="product-family-hero-overlay product-family-hero-overlay-heavy" />
        <div className="container product-family-hero-content">
          <div className="reveal eyebrow ax-hero-eyebrow">Ateno AX</div>
          <h1 className="reveal hero-headline">A coprocessor for 3D Gaussian Splatting.</h1>
          <p className="reveal product-family-subhead">
            Currently an FPGA prototype. Dedicated silicon is the next step.
          </p>
          <div className="reveal product-family-cta-row">
            <Link to="/ax#architecture" className="btn btn-white btn-lg">
              <BookOpenText size={18} strokeWidth={1.8} />
              Product status
            </Link>
            <a href="mailto:info@ateno.co" className="btn btn-lg product-family-btn-ghost">
              <Mail size={18} strokeWidth={1.8} />
              Contact Team
            </a>
          </div>
        </div>
      </section>

      <ScrollScrubVideo
        id="concept"
        src="/assets/ax/scrub/ax-concept-720p.mp4"
        mobileSrc="/assets/ax/scrub/ax-concept-480p.mp4"
        poster="/assets/ax/scrub/ax-concept-poster.jpg"
        captions={AX_CONCEPT_CAPTIONS}
        disclosure={AX_CONCEPT_DISCLOSURE}
        frames={{ basePath: "/assets/ax/scrub/frames", count: 40, pad: 4, extension: "jpg" }}
      />

      <section className="section section-dark" id="architecture">
        <div className="container-narrow">
          <div className="reveal eyebrow" id="acceleration">Product status</div>
          <h2 className="reveal headline">Ateno AX</h2>
          <p className="reveal subhead">
            Ateno AX is a coprocessor for 3D Gaussian Splatting workloads.
          </p>
          <p className="reveal subhead" id="applications">
            Currently an FPGA prototype. Dedicated silicon is the next step.
          </p>
          <div className="reveal" style={{ marginTop: 32 }}>
            <h3>Published writing</h3>
            <p className="subhead">
              Read technical writing by Ateno co-founders Sviatoslav Gladyshev and Artemii Mavlitov.
            </p>
            <ul style={{ display: "grid", gap: 20, marginTop: 20, paddingLeft: 20 }}>
              <li>
                <Link to="/research#papers" style={{ color: "var(--accent-light)" }}>
                  Ateno AI: Natural-Language Editing of Interactive 3D Object Scenes
                </Link>
                <p style={{ color: "var(--text-secondary)", marginTop: 6 }}>
                  By Artemii Mavlitov and Sviatoslav Gladyshev
                </p>
              </li>
              {editorialArticles.map((article) => (
                <li key={article.slug}>
                  <Link to={`/news/${article.slug}`} style={{ color: "var(--accent-light)" }}>
                    {article.title}
                  </Link>
                  <p style={{ color: "var(--text-secondary)", marginTop: 6 }}>
                    By {article.author}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="section section-dark" style={{ textAlign: "center" }}>
        <div className="container-narrow">
          <div className="reveal eyebrow">Contact</div>
          <h2 className="reveal headline" style={{ marginBottom: 14 }}>AX is the infrastructure story behind the stack.</h2>
          <p className="reveal subhead" style={{ maxWidth: 460, margin: "0 auto 32px" }}>
            If you are building products where spatial processing matters, AX is the layer worth discussing.
          </p>
          <div className="reveal" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="mailto:info@ateno.co" className="btn btn-white btn-lg">
              <Mail size={18} strokeWidth={1.8} />
              Contact Team
            </a>
            <Link to="/ax#architecture" className="btn btn-ghost btn-lg">
              <BookOpenText size={18} strokeWidth={1.8} />
              Product status
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
